import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";

const roleLabel = (role) => {
  if (role === "superuser") return "Super User";
  if (role === "broker") return "Broker";
  if (role === "finance") return "Finance";
  if (role === "agent") return "Agent";
  return role || "—";
};

const PasswordResetRequestsPanel = ({ onResolved }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [createdLogin, setCreatedLogin] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        "/auth/password-reset-requests?status=pending"
      );
      setRequests(res.data.requests || []);
    } catch (_) {
      toast.error("Failed to load password requests.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resend = async (request) => {
    if (
      !window.confirm(
        `Email a new password to ${request.email || request.username}?`
      )
    ) {
      return;
    }
    try {
      setWorkingId(request._id);
      const res = await axiosInstance.post(
        `/auth/password-reset-requests/${request._id}/resend`
      );
      setCreatedLogin({
        name: request.displayName || request.username,
        username: res.data.username,
        password: res.data.password,
        emailSent: res.data.emailSent,
        emailError: res.data.emailError,
      });
      if (res.data.emailSent) {
        toast.success(res.data.message);
      } else {
        toast.warning(
          res.data.emailError ||
            "Password created, but the email was not sent. Copy it below."
        );
      }
      await load();
      if (onResolved) onResolved();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to resend the password email."
      );
    } finally {
      setWorkingId(null);
    }
  };

  const dismiss = async (request) => {
    try {
      setWorkingId(request._id);
      await axiosInstance.post(
        `/auth/password-reset-requests/${request._id}/dismiss`
      );
      toast.success("Request dismissed.");
      await load();
      if (onResolved) onResolved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to dismiss request.");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-blue-900 mb-2">
        Password requests
      </h2>
      <p className="text-gray-600 mb-4">
        When a Super User, Broker, Agent, or Finance user forgets their password,
        they can request a new one from the login page. Resend the login email
        from here.
      </p>
      {createdLogin && (
        <div
          className={`mb-4 p-4 rounded-md border ${
            createdLogin.emailSent
              ? "bg-green-50 border-green-200"
              : "bg-yellow-50 border-yellow-300"
          }`}
        >
          <p className="font-semibold mb-2">
            {createdLogin.emailSent
              ? `New password emailed to ${createdLogin.name}.`
              : createdLogin.emailError ||
                "Email could not be sent. Copy these credentials."}
          </p>
          <p>
            <strong>Username:</strong> {createdLogin.username}
          </p>
          <p>
            <strong>Password:</strong> {createdLogin.password}
          </p>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-blue-900 text-white">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Username</th>
                <th className="text-left px-3 py-2">User type</th>
                <th className="text-left px-3 py-2">Requested</th>
                <th className="text-left px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-3 py-6 text-gray-500">
                    No pending password requests.
                  </td>
                </tr>
              ) : (
                requests.map((request) => (
                  <tr key={request._id} className="border-b">
                    <td className="px-3 py-2">
                      {request.displayName || "—"}
                    </td>
                    <td className="px-3 py-2">{request.username}</td>
                    <td className="px-3 py-2">{roleLabel(request.role)}</td>
                    <td className="px-3 py-2">
                      {request.requestedAt
                        ? new Date(request.requestedAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={workingId === request._id}
                          onClick={() => resend(request)}
                          className="px-3 py-1 text-sm bg-blue-900 text-white rounded hover:bg-blue-800 disabled:opacity-60"
                        >
                          {workingId === request._id
                            ? "Sending..."
                            : "Resend password email"}
                        </button>
                        <button
                          type="button"
                          disabled={workingId === request._id}
                          onClick={() => dismiss(request)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PasswordResetRequestsPanel;
